import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ClipboardList, Send, CheckCircle2, XCircle, Package } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { fmt } from '@/lib/format';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { cn } from '@/lib/cn';

export function RequisicionDetallePage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [rechazarOpen, setRechazarOpen] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [loading, setLoading] = useState('');

  const { data: r } = useQuery<any>({ queryKey: ['requisicion', id], queryFn: () => api(`/requisiciones/${id}`) });

  if (!r) return <div className="p-10 text-[13px] text-ink-mute">Cargando…</div>;

  async function accion(endpoint: string, label: string, body?: any) {
    setLoading(endpoint);
    try {
      await api(`/requisiciones/${id}/${endpoint}`, { method: 'POST', body: body ?? {} });
      qc.invalidateQueries({ queryKey: ['requisicion', id] });
      qc.invalidateQueries({ queryKey: ['requisiciones'] });
    } catch (e) {
      alert(e instanceof ApiError ? e.message : `Error ${label}`);
    } finally {
      setLoading('');
    }
  }

  const estados = ['borrador', 'solicitada', 'autorizada', 'en_compra', 'cerrada'];
  const idxActual = estados.indexOf(r.estado);
  const rechazada = r.estado === 'rechazada';

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Link to="/app/requisiciones" className="inline-flex items-center gap-1.5 text-[12px] font-medium text-ink-mute hover:text-ink">
          <ArrowLeft size={14} /> Volver a requisiciones
        </Link>
        <div className="mt-3 flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-brand-50 text-brand-600 dark:bg-brand-900/40">
              <ClipboardList size={20} />
            </div>
            <div>
              <p className="num text-[12px] font-semibold uppercase tracking-[0.14em] text-brand-700 dark:text-brand-300">{r.folio}</p>
              <h1 className="font-display text-[24px] font-bold tracking-tight text-ink">Requisición de compra</h1>
              <p className="text-[12px] text-ink-mute">{fmt.date(r.fecha)} · {r.tipo_adjudicacion}</p>
            </div>
          </div>
          <div className="flex gap-2">
            {r.estado === 'borrador' && (
              <Button onClick={() => accion('solicitar', 'solicitar')} loading={loading === 'solicitar'}>
                <Send size={14} /> Solicitar
              </Button>
            )}
            {(r.estado === 'borrador' || r.estado === 'solicitada') && (
              <>
                <Button onClick={() => accion('autorizar', 'autorizar')} loading={loading === 'autorizar'}>
                  <CheckCircle2 size={14} /> Autorizar
                </Button>
                <Button variant="ghost" onClick={() => setRechazarOpen(true)} className="text-danger">
                  <XCircle size={14} /> Rechazar
                </Button>
              </>
            )}
            {r.estado === 'autorizada' && (
              <Button onClick={() => navigate(`/app/ordenes-compra/nueva?req=${r.id}`)}>
                <Package size={14} /> Emitir orden de compra
              </Button>
            )}
          </div>
        </div>
      </div>

      {!rechazada && (
        <ol className="flex items-center gap-4 rounded-lg border border-border bg-surface p-4">
          {estados.map((e, i) => (
            <li key={e} className="flex flex-1 items-center gap-2">
              <div className={cn(
                'flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-semibold',
                i < idxActual ? 'bg-emerald-500 text-white' :
                i === idxActual ? 'bg-brand-500 text-white' :
                'bg-muted text-ink-mute'
              )}>{i + 1}</div>
              <span className={cn('text-[13px] capitalize', i === idxActual ? 'font-semibold text-ink' : 'text-ink-mute')}>
                {e.replace('_', ' ')}
              </span>
              {i < estados.length - 1 && <div className="flex-1 h-px bg-border" />}
            </li>
          ))}
        </ol>
      )}
      {rechazada && (
        <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-4 dark:border-rose-800/50 dark:bg-rose-900/20">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-rose-700 dark:text-rose-200">Rechazada</p>
          {r.motivo_rechazo && <p className="mt-1 text-[13px] text-ink">{r.motivo_rechazo}</p>}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card>
          <CardHeader title="Información" />
          <CardBody className="space-y-2 text-[13px]">
            <KV label="Folio" value={<span className="num font-semibold">{r.folio}</span>} />
            <KV label="Fecha" value={fmt.date(r.fecha)} />
            <KV label="Tipo adjudicación" value={<span className="capitalize">{r.tipo_adjudicacion}</span>} />
            <KV label="Fundamento" value={r.fundamento_legal ?? '—'} />
            <KV label="Unidad" value={r.unidad_nombre ?? '—'} />
            <KV label="Solicitante" value={r.solicitante_nombre ?? '—'} />
            <KV label="Partida COG" value={r.partida_cog_codigo ?? '—'} />
            <KV label="Presupuesto" value={r.presupuesto_egreso_id ? '✓ Vinculado' : '—'} />
            <KV label="Póliza compromiso" value={r.asiento_compromiso_id ? '✓ Emitida' : '—'} />
          </CardBody>
        </Card>
        <Card>
          <CardHeader title="Importes estimados" />
          <CardBody className="space-y-2 text-[13px]">
            <KV label="Subtotal" value={<span className="num">{fmt.money(r.subtotal_estimado)}</span>} />
            <KV label="IVA" value={<span className="num">{fmt.money(r.iva_estimado)}</span>} />
            <KV label="Total" value={<span className="num font-semibold">{fmt.money(r.total_estimado)}</span>} />
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader title="Justificación" />
        <CardBody><p className="text-[13px] leading-relaxed">{r.justificacion}</p></CardBody>
      </Card>

      <Card>
        <CardHeader title="Líneas" />
        <CardBody className="p-0">
          <table className="min-w-full text-[13px]">
            <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-mute">
              <tr>
                <th className="px-5 py-3 w-10">#</th>
                <th className="px-5 py-3">Descripción</th>
                <th className="px-5 py-3">Art.</th>
                <th className="px-5 py-3 text-right">Cantidad</th>
                <th className="px-5 py-3 text-right">P.U.</th>
                <th className="px-5 py-3 text-right">Total</th>
                <th className="px-5 py-3 text-center">AF</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(r.lineas ?? []).map((l: any) => (
                <tr key={l.id}>
                  <td className="num px-5 py-3">{l.secuencia}</td>
                  <td className="px-5 py-3">{l.descripcion}</td>
                  <td className="num px-5 py-3 text-[11px] text-brand-700">{l.articulo_clave ?? '—'}</td>
                  <td className="num px-5 py-3 text-right">{fmt.num(l.cantidad)} {l.unidad ?? ''}</td>
                  <td className="num px-5 py-3 text-right">{fmt.money(l.precio_unitario_estimado)}</td>
                  <td className="num px-5 py-3 text-right font-semibold">{fmt.money(l.total_estimado)}</td>
                  <td className="px-5 py-3 text-center">{l.es_activo_fijo ? '✓' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>

      <Modal open={rechazarOpen} onClose={() => setRechazarOpen(false)} title="Rechazar requisición"
        footer={<>
          <Button variant="ghost" type="button" onClick={() => setRechazarOpen(false)}>Cancelar</Button>
          <Button variant="danger" onClick={async () => {
            await accion('rechazar', 'rechazar', { motivo });
            setRechazarOpen(false);
          }} loading={loading === 'rechazar'}>Rechazar</Button>
        </>}>
        <div>
          <label className="text-[13px] font-medium text-ink">Motivo de rechazo</label>
          <textarea required rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)}
            className="mt-1.5 w-full rounded-md border border-border bg-surface px-3 py-2 text-[14px] shadow-subtle" />
        </div>
      </Modal>
    </div>
  );
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[160px_1fr] gap-3 border-b border-border/60 pb-1.5 last:border-0">
      <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-soft">{label}</span>
      <span className="text-ink">{value}</span>
    </div>
  );
}
